import I18n from 'I18n';
import { fromJS } from 'immutable';
import { useState } from 'react';
import { H7 } from 'foundations-components/transitional/components/H7';
import { Button } from 'foundations-components/transitional/components/Button';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { PropertyFilter } from 'customer-data-filters-ui/public/components';
// @ts-expect-error untyped import
import { useTableSource, useProperty } from '../SourceMeta';
// @ts-expect-error untyped import
import { GroupFilterControl } from '../shared/GroupFilterControl';
// @ts-expect-error untyped import
import { CloneFilterSelect } from '../shared/CloneFilterSelect';
import { htmlEscapedFilter, isHtmlFieldType } from '../../filter-utils';
import { htmlNormalizedToTextFilter } from '../../filter-utils';
import debounce from 'react-utils/debounce';
import { useSnowflakeObjectStrategy } from '../../strategies/use-snowflake-object-strategy';
import { getFilterFamily, getReportingFilterContext } from '../../../../utils/filter-utils';
import { DataSourceTypes } from '../../../../schema/source-records';
import { isRelativeTimeProperty } from '../../../../utils/column-utils';
import { useFilterRelevantUserInfo } from '../../../../../shared/lib/utils';
// @ts-expect-error untyped import
import { shouldUseLegacyDateTimeFilter } from '../../xo-filter-utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const NewPropertyFilterPanel = ({
  filter,
  groupId,
  onDone,
  onCancel,
  onCloneFilter
}) => {
  const {
    field: {
      table: tableName,
      name: fieldName
    }
  } = filter;
  const propertyId = filter.filter.get('property');
  const source = useTableSource(tableName);
  const property = useProperty(tableName, fieldName);
  const [draftFilter, setDraft] = useState(filter);
  const [isValidFilter, setIsValidFilter] = useState(true);
  const {
    userGates
  } = useFilterRelevantUserInfo();
  if (property == null) {
    throw new Error('PropertyFilterPanel can only be rendered when there is a matching existing property in report meta!');
  }
  if (source == null) {
    throw new Error('PropertyFilterPanel can only be rendered when there is a table description in the report!');
  }
  let {
    filter: propertyFilter
  } = draftFilter;
  if (isHtmlFieldType(property)) {
    propertyFilter = htmlNormalizedToTextFilter(draftFilter).filter;
  }
  const isVirtualDataSource = source.dataSourceType === DataSourceTypes.VIRTUAL_DATASOURCE;

  // The property naming convention for virtual data sources is:
  // <object type family>/<object type name>/<property name>, for eg, AD_CAMPAIGN/hs_ad_account_id
  // We need to use the object type family as the filter family for virtual data sources
  const filterFamily = isVirtualDataSource && property.name.includes('/') ? property.name.split('/')[0] : getFilterFamily(source);
  const [draftGroupId, setGroup] = useState(groupId);
  const isSubmitDisabled = !isValidFilter;
  const reportingFilterContext = getReportingFilterContext(source, property);
  const isRtp = isRelativeTimeProperty(property);
  const shouldUseLegacyDateFilter = shouldUseLegacyDateTimeFilter(propertyFilter, userGates);
  const strategy = useSnowflakeObjectStrategy({
    source,
    reportingFilterContext,
    filterFamilyOverride: filterFamily,
    isRelativeTimeProperty: isRtp,
    shouldUseLegacyDateFilter,
    userGates
  });
  const handleChange = (value, info) => {
    if (!value || info.hasIncompleteFilters || info.hasFilterValidationErrors) {
      setIsValidFilter(false);
      return;
    }
    const nextFilter = filter.set('filter', propertyId ? fromJS(value).set('property', propertyId) : fromJS(value));
    setIsValidFilter(true);
    if (isHtmlFieldType(property)) {
      const updatedFilter = htmlEscapedFilter(nextFilter);
      setDraft(updatedFilter);
    } else {
      setDraft(nextFilter);
    }
  };
  const debouncedHandleChange = debounce(handleChange, 300);
  const handleDone = () => {
    onDone({
      draftFilter,
      draftGroupId
    });
  };
  const handleCloneFilter = groupIndex => {
    onCloneFilter({
      draftFilter,
      groupId: groupIndex
    });
  };
  const disabledButtonTooltipCopy = I18n.text('reporting-snowflake.filter-editor.editor-panel.incomplete-filter-options');
  if (!strategy) {
    return null;
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "filter-edit-panel",
    children: [/*#__PURE__*/_jsx(H7, {
      marginBlockEnd: "0",
      children: property.label || property.name
    }), /*#__PURE__*/_jsx(PropertyFilter, {
      requiredPropertyName: property.name,
      filterFamily: filterFamily,
      onChange: debouncedHandleChange,
      strategy: strategy,
      value: propertyFilter.set('property', property.name).toJS(),
      use: "vertical",
      hidePropertySelect: true
    }), /*#__PURE__*/_jsx(GroupFilterControl, {
      currentGroupId: draftGroupId,
      onGroupSelected: nextGroupId => setGroup(nextGroupId)
    }), /*#__PURE__*/_jsxs(TFlex, {
      justify: "between",
      marginBlockStart: "400",
      children: [/*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(Tooltip, {
          title: disabledButtonTooltipCopy,
          placement: "top right",
          disabled: !isSubmitDisabled,
          children: /*#__PURE__*/_jsx(Button, {
            use: "tertiary",
            size: "xs",
            "data-test-id": "filter-editor-panel-apply-btn",
            disabled: isSubmitDisabled,
            onClick: handleDone,
            children: I18n.text('reporting-snowflake.filter-editor.editor-panel.apply')
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "tertiary-light",
          size: "xs",
          onClick: onCancel,
          children: I18n.text('reporting-snowflake.filter-editor.editor-panel.cancel')
        })]
      }), /*#__PURE__*/_jsx(CloneFilterSelect, {
        currentGroupId: groupId,
        disabled: isSubmitDisabled,
        tooltipMessage: disabledButtonTooltipCopy,
        onCloneFilter: handleCloneFilter
      })]
    })]
  });
};